import PropTypes from 'prop-types'
import { useState, useEffect } from 'react'
import HighSchoolLessonStatusIcon from 'components/dashboard/HighSchoolLessonStatusIcon'
import {
  StyledAccordion,
  CollectionCardTitle,
  IconWrapper,
  IconWrapperForCard,
  CollectionCard,
  CollectionCardTitleWithIcon,
  DescriptionText,
  ResourceListing,
  ResourceItem,
  ResourceTitle,
} from './component.styles'

function StaticCollectionCard({
  isComplete,
  // this flag is not always passed to the component:
  isSkipped = false,
  navigationEvent,
  path,
  title,
  description,
  children,
  startExpanded = false,
  resetAccordionSignal,
  useAccordion = false,
  pathwayWeeks,
  onResourceClick,
}) {
  const [isActiveFromClick, setIsActiveFromClick] = useState(false)

  useEffect(() => {
    // even if the accordion has been open, reset it to closed because we changed tabs:
    setIsActiveFromClick(false)
  }, [resetAccordionSignal])

  const handleNavigation = () => {
    // If there's a path, navigate to it when clicked
    if (path && navigationEvent) {
      navigationEvent(path)
    }
  }

  const handleResourceClick = routeSlug => {
    // Use the provided handler or fall back to default navigation
    if (onResourceClick) {
      onResourceClick(routeSlug)
    } else if (routeSlug && navigationEvent) {
      // Fallback to default navigation if no custom handler provided
      navigationEvent(`/product/high-school/learning-experiences/${routeSlug}`)
    }
  }

  // Accordion version
  if (useAccordion) {
    return (
      <StyledAccordion
        followActiveProp
        // the accordion is smart enough to handle clicks on its own...
        // but we need to track the state from clicks so that the followActiveProp functionality works
        isActive={startExpanded || isActiveFromClick}
        onClick={() => setIsActiveFromClick(!isActiveFromClick)}
        title={
          <CollectionCardTitle>
            <IconWrapper>
              <HighSchoolLessonStatusIcon
                isComplete={isComplete}
                isSkipped={isSkipped}
              />
            </IconWrapper>
            {title}
          </CollectionCardTitle>
        }
      >
        {description && <DescriptionText>{description}</DescriptionText>}

        {/* Display pathway collection data */}
        {pathwayWeeks && pathwayWeeks.length > 0 && (
          <ResourceListing>
            {pathwayWeeks.map((collection, index) => (
              <ResourceItem
                key={index}
                onClick={e => {
                  e.stopPropagation() // Prevent accordion toggle
                  handleResourceClick(collection.collectionRouteSlug)
                }}
                type="button"
              >
                <ResourceTitle>
                  {collection.collectionDisplayTitle}
                </ResourceTitle>
              </ResourceItem>
            ))}
          </ResourceListing>
        )}

        {children}
      </StyledAccordion>
    )
  }

  // Non-accordion version (simple clickable card)
  return (
    <CollectionCard onClick={handleNavigation}>
      <CollectionCardTitleWithIcon>
        <IconWrapperForCard>
          <HighSchoolLessonStatusIcon
            isComplete={isComplete}
            isSkipped={isSkipped}
          />
        </IconWrapperForCard>
        {title}
      </CollectionCardTitleWithIcon>
    </CollectionCard>
  )
}

StaticCollectionCard.propTypes = {
  children: PropTypes.node,
  description: PropTypes.string,
  isComplete: PropTypes.bool,
  isSkipped: PropTypes.bool,
  navigationEvent: PropTypes.func,
  onResourceClick: PropTypes.func,
  path: PropTypes.string,
  pathwayWeeks: PropTypes.array,
  resetAccordionSignal: PropTypes.bool,
  startExpanded: PropTypes.bool,
  title: PropTypes.string,
  useAccordion: PropTypes.bool,
}

export default StaticCollectionCard
